import { zodResolver } from '@hookform/resolvers/zod';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import MuiFormSelect from 'components/Form/MuiFormSelect/MuiFormSelect';
import type { MuiFormSelectOptionT } from 'components/Form/MuiFormSelect/MuiFormSelect';
import Popup from 'components/Popup/Popup';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';

import useImportSchedulePopup from './hooks/useImportSchedulePopup';
import {
  FlexContainerControlsStyled,
  FormStyled,
  getPopupContentStyle,
} from './ImportSchedulePopup.styled';
import validationSchema from './validationSchema';

const ImportSchedulePopup: FC = () => {
  const { isImportSchedulePopupOpened, handleClosePopup, onSubmit, isUploading } =
    useImportSchedulePopup();
  const { isMobile } = useResponsive();
  const { selectedRoofTop, roofTopsList, dealerSelectedRooftopIds, setSelectedRoofTop } =
    useTickets();

  const dealerOptions: MuiFormSelectOptionT[] = useMemo((): MuiFormSelectOptionT[] => {
    const refinedIds = dealerSelectedRooftopIds
      ? dealerSelectedRooftopIds
          .split(',')
          .map((id: string) => id.trim())
          .filter(Boolean)
      : [];
    const baseList =
      refinedIds.length > 0 && roofTopsList
        ? roofTopsList.filter((r: { id: string }) => refinedIds.includes(r.id))
        : roofTopsList ?? [];
    return baseList
      .map((r: { id: string; name: string }) => ({ value: r.id, label: r.name }))
      .sort((a: MuiFormSelectOptionT, b: MuiFormSelectOptionT) => a.label.localeCompare(b.label));
  }, [roofTopsList, dealerSelectedRooftopIds]);

  const formMethods = useForm<{ csv: File[]; roofTopId: string }>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      csv: [],
      roofTopId: selectedRoofTop ?? '',
    },
  });
  const { control, reset } = formMethods;
  const attachmentFileValue = useWatch({
    control,
    name: 'csv',
  });

  const [isUploadScheduleFilenameError, setIsUploadScheduleFilenameError] = useState(false);
  const [uploadScheduleFilenameMessage, setUploadScheduleFilenameMessage] = useState('');

  const attachmentFileName = attachmentFileValue
    ? Array.from(attachmentFileValue as FileList | File[])
        .map((file: File) => file.name)
        .join(', ')
    : '';

  useEffect(() => {
    const filesArray = attachmentFileValue
      ? Array.from(attachmentFileValue as FileList | File[])
      : [];
    if (filesArray.length > 0) {
      const hasInvalidFilename = filesArray.some(file => {
        return /[[\]]/.test(file.name) || file.name.startsWith('_'); // Restricts [, ], and _
      });

      setIsUploadScheduleFilenameError(hasInvalidFilename);
      setUploadScheduleFilenameMessage(
        hasInvalidFilename ? "Filenames cannot contain '[', ']', or start with '_'" : ''
      );
    } else {
      setIsUploadScheduleFilenameError(false);
      setUploadScheduleFilenameMessage('');
    }
  }, [attachmentFileValue]);

  const { handleSubmit } = formMethods;

  useEffect(() => {
    if (isImportSchedulePopupOpened) {
      reset({ csv: [], roofTopId: selectedRoofTop ?? '' });
    }
  }, [isImportSchedulePopupOpened, reset, selectedRoofTop]);

  return (
    <Popup
      showCloseBtn
      modalTitle="Import Schedule"
      size={ModalSizeE.IMPORT_SCHEDULE}
      contentStyle={getPopupContentStyle(isMobile)}
      popupId={ScrollLockIdE.ImportSchedulePopup}
      isOpen={isImportSchedulePopupOpened}
      onClose={handleClosePopup}
      closeOnEscape={false}
    >
      <FormProvider {...formMethods}>
        <FormStyled role="form" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mb: 2 }} data-testid="import-schedule-rooftop-select">
            <MuiFormSelect
              required
              searchable
              label="Dealer"
              name="roofTopId"
              placeholder=""
              options={dealerOptions}
              onChange={(value): void => setSelectedRoofTop(value as string)}
            />
          </Box>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="label" htmlFor="import-schedule-file-input" sx={{ cursor: 'pointer' }}>
              <input
                {...formMethods.register('csv')}
                type="file"
                accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel"
                id="import-schedule-file-input"
                data-testid="import-schedule-file-input"
                style={{ display: 'none' }}
              />
              <Tooltip title="Choose file" arrow>
                <IconButton
                  component="span"
                  aria-label="Choose file"
                  sx={{
                    color: attachmentFileName
                      ? (t): string => t.palette.success.main
                      : (t): string => t.palette.primary.main,
                  }}
                >
                  <AttachFileIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography
              variant="body2"
              sx={{
                minWidth: 0,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {attachmentFileName || 'Attachment not selected'}
            </Typography>
          </Box>
          {isUploadScheduleFilenameError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {uploadScheduleFilenameMessage}
            </Typography>
          )}
          <FlexContainerControlsStyled justify={isMobile ? 'center' : 'flex-end'}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                isUploading || !formMethods.watch('roofTopId') || isUploadScheduleFilenameError
              }
              startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Publish
            </Button>
          </FlexContainerControlsStyled>
        </FormStyled>
      </FormProvider>
    </Popup>
  );
};

export default ImportSchedulePopup;
